/**
 * What deleting a company DESTROYS, said in one place.
 *
 * ⚠️ THIS CHANGED ON 2026-08-30 AND IT CHANGED IN THE DANGEROUS DIRECTION.
 * Until backend migration 111, a company holding notes could not be deleted at
 * all: `fk_notes_company` was `ON DELETE RESTRICT` and the service answered a
 * `409` naming the count. Both delete confirmations were built on that fact and
 * `CompanyDetailPage` said so in a comment — it deliberately pre-fetched
 * NOTHING, on the reasoning that a refused delete loses nothing, so there was
 * nothing to warn about.
 *
 * The owner's instruction ("I am not able to delete a company which has notes in
 * it, relax this rule") reversed it. The notes now CASCADE, and this dialog is
 * the only place anybody is told before years of an account's written history
 * are destroyed with no undo and nothing to restore from.
 *
 * ── ONE COPY, DELIBERATELY ────────────────────────────────────────────────
 *
 * Two mount points — the row action on `/companies` and the detail page — and
 * the same rule everywhere else in this app applies: two copies agree the day
 * they are written and drift on the first correction, and what would drift here
 * is a sentence about whether a customer's account history was destroyed.
 *
 * ── ⚠️ TASKS ARE NOT THIS SENTENCE'S BUSINESS ─────────────────────────────
 *
 * `fk_tasks_company` is untouched and still `ON DELETE RESTRICT`, so a company
 * holding tasks is still REFUSED with `COMPANY_HAS_TASKS_OR_NOTES` (the code is
 * unchanged even though it can now only mean tasks — a deployed client branches
 * on it). Tasks are therefore not destroyed and must never appear in a
 * "this will be deleted" sentence: promising to destroy something that in fact
 * blocks the operation is wrong in both directions at once. The server's own
 * refusal names the tasks if there are any, at the moment of the attempt.
 */

/**
 * The consequence line for a company's notes. Never `null` — the delete always
 * destroys whatever is there, so there is always something to say.
 *
 * @param visibleNoteTotal how many HUMAN notes the VIEWER CAN SEE, from
 *   `GET /notes/summary`'s `count`, or `null` when that has not been read.
 * @param systemNoteTotal how many notes SEED EMERGE wrote itself, from the same
 *   response's `systemCount`. **Pass `0` when the key is absent** — see the note
 *   on the two figures below.
 *
 * ══ 🔴 THE FIGURE IS A FLOOR, NOT A TOTAL, AND THE WORDING DEPENDS ON IT ══
 *
 * `notes.repository.summary()` applies `visibilityClause(actorId, canSeeAll)`,
 * exactly as every other notes read does. **Without the audit grant at scope
 * `all`, the count is the viewer's OWN notes and nobody else's.** Most staff do
 * not hold that grant.
 *
 * The CASCADE has no such clause. It destroys every note on the account.
 *
 * So a colleague's forty notes are deleted by a person whose dialog counted two
 * — and, worse, by a person whose dialog counted **zero**. This is the identical
 * failure `/university-partnerships` already shipped once, where the same
 * per-viewer figure was rendered as an account-level "last activity" and read
 * differently for different people.
 *
 * Two rules follow, and both invert what the obvious implementation does:
 *
 *   1. **ZERO IS NOT SILENCE.** "No notes are recorded" is the single most
 *      dangerous sentence this dialog could print, because a viewer seeing none
 *      of somebody else's forty gets a clean bill of health immediately before
 *      destroying them. Zero is worded exactly like unknown.
 *   2. **A COUNT IS ONLY EVER "AT LEAST".** It is never presented as the
 *      account's total, and the sentence says out loud that colleagues' notes go
 *      too and are not in the figure.
 *
 * An exact total would need a count that ignores visibility, which no endpoint
 * offers. **Do not synthesise one by paging `/notes`** — it is scoped the same
 * way, so it would produce the same floor at more expense.
 *
 * ⚠️ **The unscoped number DOES exist and is already computed.**
 * `companies.repository.countTaskAndNoteReferences` selects tasks and notes in
 * one round trip with no visibility clause and no `is_system` predicate, and
 * the service destructures `const { tasks } = …` and throws the notes figure
 * away. Nothing returns it to a client. **Exposing it is a backend decision, not
 * a consistency fix** — until then this floor is the honest ceiling of what this
 * dialog can say.
 *
 * ⚠️ `null` IS BOTH "STILL LOADING" AND "THE READ FAILED", deliberately not
 * separated — so its sentence must be TRUE IN BOTH. An earlier draft read "how
 * many notes this account holds could not be read", which is false for the
 * moment between the dialog opening and the count arriving, i.e. every time.
 *
 * ══ 🔴 THE TWO FIGURES ARE SCOPED DIFFERENTLY AND MUST NEVER BE SUMMED ══
 *
 * Added 2026-09-19, owner's decision. `/notes/summary` returns both:
 *
 *   - **`count`** — human notes, `COUNT(CASE WHEN n.is_system = 0 …)`, behind
 *     `visibilityClause`. 🔴 **VIEWER-SCOPED. A FLOOR.**
 *   - **`systemCount`** — `COUNT(CASE WHEN n.is_system = 1 …)`. The second arm
 *     of `visibilityClause` is `OR n.is_system = 1`, so **every reader sees
 *     every system note** and this figure is EXACT and the same for everybody.
 *
 * ⛔ **So `count + systemCount` is a floor added to an exact number, presented
 * as one total.** It is only the true total for an audit-grant holder, and
 * nothing on the wire says which kind of reader is looking. The two are rendered
 * as TWO FACTS, in two clauses, and the "at least" stays on the human one.
 *
 * ⛔ **NEVER DERIVE ONE BY SUBTRACTION.** They run through different rules — one
 * narrowed by `HUMAN_NOTES_ONLY`, one not — so the arithmetic crosses two
 * populations.
 *
 * ⚠️ **`count: 0, systemCount: 1` IS REACHABLE**, and is new since 2026-09-14: a
 * record whose only notes are automatic. Rule 1 still binds there — the zero is
 * not silence — so that state gets a sentence naming what goes, not the bare
 * unknown line.
 *
 * ⚠️ **`systemNoteTotal` is typed required and can still be ABSENT at runtime**
 * from a server predating the field. Callers fold that with
 * `typeof s === 'number' ? s : 0` and **not `?? 0`**: a real `0` and an absent
 * key both mean "say nothing about automatic notes", and only one of them is a
 * guess.
 */
export function companyNotesConsequence(
  visibleNoteTotal: number | null,
  systemNoteTotal: number,
): string {
  const system = systemNoteTotal > 0
    ? `${systemNoteTotal} ${systemNoteTotal === 1 ? 'note' : 'notes'} SEED EMERGE wrote`
    : null;

  // Unknown and zero share a sentence ON PURPOSE — see rule 1 above. `?? 0` or a
  // `> 0` guard that falls through to silence would undo the whole point.
  if (visibleNoteTotal === null || visibleNoteTotal === 0) {
    // The system half is EXACT even here, so it can be named outright — but the
    // human half is still unknown-or-zero, so "any notes" has to carry it.
    return system === null
      ? 'Any notes on it are permanently deleted and cannot be restored.'
      : `Any notes on it are permanently deleted and cannot be restored — including the ${system}.`;
  }

  const human = `At least ${visibleNoteTotal} ${
    visibleNoteTotal === 1 ? 'note' : 'notes'
  } will be permanently deleted — colleagues' notes go too, and are not counted here`;

  return system === null
    ? `${human}. None of it can be restored.`
    : `${human} — and so ${systemNoteTotal === 1 ? 'does' : 'do'} the ${system}. None of it can be restored.`;
}
